import { paymentService } from '@/services/payment.service';
import { useAuthStore } from '@/store/auth.store';
import { SubscriptionStatus } from '@/types/auth.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Query Keys
export const paymentKeys = {
  plans: ['payments', 'plans'] as const,
  subscription: ['payments', 'subscription'] as const,
  paymentMethods: ['payments', 'methods'] as const,
  invoices: ['payments', 'invoices'] as const,
};

// Hook to get the single plan
export const usePlan = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: paymentKeys.plans,
    queryFn: paymentService.getPlans,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  // Since there's only one plan, return it directly
  const plan = data?.data?.[0] || null;

  // Calculate monthly price from yearly

  return {
    plan,
    isLoading,
    error,
  };
};

// Alias for backward compatibility
export const usePlans = usePlan;

// Hook for current subscription with subscription details
export const useSubscription = () => {
  const { user } = useAuthStore();
  const subscriptionQuery = useQuery({
    queryKey: paymentKeys.subscription,
    queryFn: paymentService.getCurrentSubscription,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user,
  });

  // Get subscription from query or user store
  const subscription = subscriptionQuery.data?.data || user?.subscription;

  const hasSubscription = !!subscription;
  const hasActiveSubscription =
    subscription?.status === SubscriptionStatus.ACTIVE;
  const isTrialing = subscription?.status === SubscriptionStatus.TRIALING;
  const isPastDue = subscription?.status === SubscriptionStatus.PAST_DUE;
  const isCanceled = subscription?.status === SubscriptionStatus.CANCELED;

  // Check if user is in grace period (past due but still has access)
  const isInGracePeriod =
    isPastDue && subscription?.currentPeriodEnd
      ? new Date(subscription.currentPeriodEnd) > new Date()
      : false;

  const needsPayment =
    !hasActiveSubscription && !isTrialing && !isInGracePeriod;

  return {
    ...subscriptionQuery,
    subscription,
    hasSubscription,
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    isCanceled,
    isInGracePeriod,
    needsPayment,
  };
};

// Hook to create Stripe checkout session
export const useCreateCheckoutSession = () => {
  return useMutation({
    mutationFn: paymentService.createCheckoutSession,
    onSuccess: response => {
      console.log('Checkout session created successfully');
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    },
    onError: (error: Error) => {
      console.error(error.message || 'Failed to create checkout session');
    },
  });
};

// Hook to cancel subscription
export const useCancelSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: paymentService.cancelSubscription,
    onSuccess: () => {
      console.log('Subscription cancelled successfully');
      queryClient.invalidateQueries({ queryKey: paymentKeys.subscription });
    },
    onError: (error: Error) => {
      console.error(error.message || 'Failed to cancel subscription');
    },
  });
};

// Hook for invoices
export const useInvoices = () => {
  return useQuery({
    queryKey: paymentKeys.invoices,
    queryFn: paymentService.getInvoices,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDownloadInvoice = () => {
  return useMutation({
    mutationFn: (invoiceId: string) =>
      paymentService.downloadInvoice(invoiceId),
    onSuccess: (blob, invoiceId) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

// Subscription utilities (for backward compatibility)
export const useSubscriptionDetails = () => {
  const { user } = useAuthStore();

  const hasSubscription = !!user?.subscription;
  const hasActiveSubscription =
    user?.subscription?.status === SubscriptionStatus.ACTIVE;
  const isTrialing = user?.subscription?.status === SubscriptionStatus.TRIALING;
  const isPastDue = user?.subscription?.status === SubscriptionStatus.PAST_DUE;
  const isCanceled = user?.subscription?.status === SubscriptionStatus.CANCELED;

  // Check if user is in grace period (past due but still has access)
  const isInGracePeriod =
    isPastDue && user?.subscription?.currentPeriodEnd
      ? new Date(user.subscription.currentPeriodEnd) > new Date()
      : false;

  const needsPayment =
    !hasActiveSubscription && !isTrialing && !isInGracePeriod;

  return {
    subscription: user?.subscription,
    hasSubscription,
    hasActiveSubscription,
    isTrialing,
    isPastDue,
    isCanceled,
    isInGracePeriod,
    needsPayment,
  };
};
